import { Link, useParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import PageHero from "@/components/feature/PageHero";
import CtaBanner from "@/components/feature/CtaBanner";
import Seo from "@/components/feature/Seo";
import JsonLd from "@/components/feature/JsonLd";
import FaqAccordion from "@/components/feature/FaqAccordion";
import Button from "@/components/base/Button";
import { services } from "@/data/services";
import NotFound from "@/pages/NotFound";

export default function ServiceDetail() {
  const { slug } = useParams<{ slug: string }>();
  const service = services.find((item) => item.slug === slug);

  if (!service) {
    return <NotFound />;
  }

  const related = services.filter((item) => item.slug !== service.slug && item.category === service.category).slice(0, 3);

  return (
    <Layout>
      <Seo
        title={`${service.title} in Trichy | Ortho Spine Care`}
        description={service.summary}
        path={`/services/${service.slug}`}
        image={service.image}
      />
      <JsonLd
        id="ld-service"
        data={{
          "@context": "https://schema.org",
          "@type": "MedicalProcedure",
          name: service.title,
          description: service.summary,
          url: `https://orthospine.care/services/${service.slug}`,
        }}
      />

      <PageHero
        title={service.title}
        subtitle={service.summary}
        crumbs={[{ label: "Home", to: "/" }, { label: "Services", to: "/services" }, { label: service.title }]}
      />

      <section className="bg-background-50 py-14 md:py-20">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-4 md:px-6 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-8">
            <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">Overview</h2>
            <p className="mt-4 font-body text-sm leading-relaxed text-foreground-700 md:text-base">{service.overview}</p>

            <div className="mt-8 h-[280px] w-full overflow-hidden rounded-lg border border-background-200 md:h-[360px]">
              <img
                src={service.image}
                alt={service.imageAlt}
                title={`${service.title} in Trichy`}
                className="h-full w-full object-cover object-top"
              />
            </div>

            <h3 className="mt-10 font-heading text-lg font-semibold text-foreground-950">What this includes</h3>
            <ul className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {service.highlights.map((item) => (
                <li key={item} className="flex items-start gap-3 font-body text-sm text-foreground-700">
                  <span className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-accent-100 text-accent-700">
                    <i className="ri-check-line text-xs" aria-hidden="true"></i>
                  </span>
                  {item}
                </li>
              ))}
            </ul>

            <h3 className="mt-10 font-heading text-lg font-semibold text-foreground-950">Treatment approach</h3>
            <ul className="mt-4 space-y-3">
              {service.treatments.map((item) => (
                <li key={item} className="flex items-start gap-3 font-body text-sm text-foreground-700">
                  <span className="mt-1.5 h-1.5 w-1.5 flex-none rounded-full bg-primary-500" aria-hidden="true"></span>
                  {item}
                </li>
              ))}
            </ul>

            <h3 className="mt-10 font-heading text-lg font-semibold text-foreground-950">Frequently asked</h3>
            <div className="mt-3 rounded-lg border border-background-200 bg-background-100/50 px-6 py-2">
              <FaqAccordion items={service.faqs} idPrefix={`service-${service.slug}`} />
            </div>
          </div>

          <aside className="lg:col-span-4">
            <div className="rounded-lg border border-background-200 bg-background-100/60 p-6">
              <h2 className="font-heading text-base font-semibold text-foreground-950">Book this service</h2>
              <p className="mt-2 font-body text-sm leading-relaxed text-foreground-600">
                Request an appointment to discuss {service.title.toLowerCase()} with our specialist.
              </p>
              <div className="mt-4">
                <Button to="/appointment" icon="ri-calendar-check-line" fullWidth>
                  Book Appointment
                </Button>
              </div>
            </div>

            <div className="mt-5 rounded-lg border border-background-200 bg-background-50 p-6">
              <h2 className="font-heading text-base font-semibold text-foreground-950">Related services</h2>
              <ul className="mt-4 space-y-3">
                {related.map((item) => (
                  <li key={item.slug}>
                    <Link
                      to={`/services/${item.slug}`}
                      className="flex items-center justify-between gap-3 font-body text-sm text-foreground-700 transition-colors hover:text-primary-700"
                    >
                      {item.title}
                      <i className="ri-arrow-right-line text-primary-600" aria-hidden="true"></i>
                    </Link>
                  </li>
                ))}
              </ul>
              <Link
                to="/services"
                className="mt-5 inline-flex items-center gap-1.5 font-label text-sm font-semibold text-primary-700 hover:text-primary-800"
              >
                View all services
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </Link>
            </div>
          </aside>
        </div>
      </section>

      <CtaBanner />
    </Layout>
  );
}